import {
  Component,
  OnInit,
  inject,
  signal,
  input,
  output,
  computed,
  ChangeDetectionStrategy,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';

import { MatTableDataSource } from '@angular/material/table';
import { translate } from '@jsverse/transloco';
import { FileItem } from 'ng2-file-upload';
import { DropdownObject, EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { FileUploadComponent } from 'app/components/file-upload/file-upload.component';
import { LexXiModalComponent } from 'app/components/xi-components/lex-xi-modal/lex-xi-modal.component';
import { SemantriaConfiguration } from 'app/providers/semantria-api/configuration.service';
import {
  AnalysisGroup,
  AnalysisGroupService,
} from 'app/providers/storage-api/analysis-group-service';
import { Analysis, AnalysisService } from 'app/providers/storage-api/analysis.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { Lookup, LookupService } from 'app/providers/storage-api/lookup.service';
import { Project } from 'app/providers/storage-api/project.service';
import { AlertService } from 'app/services/alert.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { LocalStorageService } from '../../../../services/local-storage.service';
import { generalNameValidator } from '../../../../utils/customValidators';
import { ProjectCollection } from '../../models/project-collection.model';

export interface AnalysisGroupSelector {
  id?: string;
  lookupId?: string;
  name: string;
  collections: CollectionTableRow[];
  journeyDefaultId: string;
}

class AddAnalysesError extends Error {
  constructor(public readonly cause: unknown) {
    super();
  }
}

interface CollectionTableRow {
  collectionName: string;
  collectionContentChannel: string;
  displayName: string;
  analysis: Analysis;
  isSelected?: boolean;
  isAutoAnalyze?: boolean;
  doesNotHaveJourneyId: boolean;
  doesNotHaveDateData: boolean;
  doesNotHaveContentChannel: boolean;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-new-analysis-group',
  templateUrl: './new-analysis-group.component.html',
  styleUrls: ['./new-analysis-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(window:beforeunload)': 'clearFilter()' },
  imports: [AppSharedModule, FileUploadComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class NewAnalysisGroupComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  readonly data = input.required<{
    configurations: Map<string, SemantriaConfiguration>;
    project: Project;
    collections: ProjectCollection[];
    analysisGroup: AnalysisGroup;
  }>();

  readonly modalClosed = output<AnalysisGroupSelector | void>();

  async clearFilter() {
    await this.clearFilterQueue(true);
  }

  isSaving = signal(false);
  inEditMode = signal(false);
  analysisGroupSelectionData = signal<AnalysisGroupSelector>({
    name: null,
    collections: [],
    journeyDefaultId: undefined,
  });
  groupNameStatus = signal<ErrorObject>({ state: EnumErrorStates.SUCCESS, text: '' });

  displayedColumns = signal<string[]>([
    'collection',
    'configuration',
    'name',
    'date',
    'documents',
    'actions',
  ]);
  tableDataSource: MatTableDataSource<CollectionTableRow>;
  rowSpans = signal<number[]>([]);

  selectedCollections = computed(() =>
    this.analysisGroupSelectionData().collections.map((coll) => coll.collectionName)
  );
  filteredDocumentsByAnalysis = signal(new Map<string, number>());
  selectedDocumentCount = computed(() =>
    this.analysisGroupSelectionData().collections.reduce(
      (count, row) => count + row.analysis.documentCount,
      0
    )
  );
  filteredDocumentCount = computed(() => {
    const file = this.filterFile();
    if (!file?.lookupId) {
      return 0;
    }
    const map = this.filteredDocumentsByAnalysis();
    return this.analysisGroupSelectionData().collections.reduce(
      (count, row) => count + (map.get(row.analysis.name) ?? 0),
      0
    );
  });

  filterFile = signal<{ name: string; lookupId?: string; count?: number } | undefined>(undefined);
  isFilterProcessing = signal(false);
  filterQueue = signal<string[]>([]);
  isSaveDisabled = computed(() => this.saveDisabled());
  isJourneyGroup = signal(false);
  journeyIdFields = signal<DropdownObject[]>([]);

  private readonly localStorageService = inject(LocalStorageService);
  private readonly analysisGroupService = inject(AnalysisGroupService);
  private readonly lookupService = inject(LookupService);
  private readonly analysisService = inject(AnalysisService);
  private readonly lexXiModal = inject(LexXiModalComponent);
  private readonly fieldService = inject(FieldService);
  private readonly alertService = inject(AlertService);

  ngOnInit() {
    this.lexXiModal.onClose = () => {
      this.onFilterFileRemove();

      this.lexXiModal.close.emit(null);
    };

    if (this.data().analysisGroup) {
      this.inEditMode.set(true);
      const ag = this.data().analysisGroup;
      this.analysisGroupSelectionData.update((prev) => ({
        ...prev,
        id: ag.id,
        name: ag.name,
        journeyDefaultId: ag.journeyGroupRule?.defaultField,
      }));
      this.isJourneyGroup.set(!!ag.journeyGroupRule?.defaultField);

      if (ag.lookupId) {
        this.analysisGroupSelectionData.update((prev) => ({ ...prev, lookupId: ag.lookupId }));
        this.lookupService.getLookup(ag.lookupId).then((res) => {
          this.onFilterFileUploaded(res);
        });
      }
    }

    this.loadGroupData();
    this.loadIdFields();
  }

  private loadGroupData() {
    const rows: CollectionTableRow[] = this.data().collections.flatMap((collection) =>
      collection.analyses
        .filter((analysis) => analysis.status !== 'ERROR')
        .map((analysis) => ({
          collectionName: collection.data.collectionName,
          collectionContentChannel: collection.data.contentChannel,
          displayName: collection.data.displayName,
          analysis,
          isSelected: this.analysisSelected(analysis),
          isAutoAnalyze: collection.data.isAutoAnalyze,
          doesNotHaveJourneyId:
            this.analysisSelected(analysis) && this.analysisDoesNotHaveJourneyId(analysis),
          doesNotHaveDateData:
            this.analysisSelected(analysis) && this.analysisDoesNotHaveDateData(analysis),
          doesNotHaveContentChannel:
            this.analysisSelected(analysis) &&
            this.collectionDoesNotHaveContentChannel(collection.data.contentChannel),
        }))
    );

    this.tableDataSource = new MatTableDataSource(rows);
    this.sortData();

    this.updateGroupData();
  }

  private setInvalidSourcesForJourney() {
    this.tableDataSource.data.forEach((row) => {
      row.doesNotHaveJourneyId = row.isSelected && this.analysisDoesNotHaveJourneyId(row.analysis);
      row.doesNotHaveDateData = row.isSelected && this.analysisDoesNotHaveDateData(row.analysis);
      row.doesNotHaveContentChannel =
        row.isSelected && this.collectionDoesNotHaveContentChannel(row.collectionContentChannel);
    });
    this.tableDataSource.data = [...this.tableDataSource.data];
    this.updateGroupData();
  }

  private analysisDoesNotHaveJourneyId(analysis: Analysis): boolean {
    const journeyDefaultId = this.analysisGroupSelectionData().journeyDefaultId;
    return !!journeyDefaultId && !analysis.customFields?.includes(journeyDefaultId);
  }

  private analysisDoesNotHaveDateData(analysis: Analysis): boolean {
    return this.isJourneyGroup() && !analysis.hasDateData;
  }

  private collectionDoesNotHaveContentChannel(contentChannel: string): boolean {
    return this.isJourneyGroup() && !contentChannel;
  }

  private analysisSelected(analysis: Analysis): boolean {
    return (
      this.inEditMode() &&
      this.data().analysisGroup?.analyses.some((groupAnalysis) =>
        [groupAnalysis.name, groupAnalysis.analysisName].includes(analysis.name)
      )
    );
  }

  private async loadIdFields() {
    try {
      const allFields = await this.fieldService.getFields(false);
      const idFields = allFields.items.filter((field) => field.identityField === true);

      const analysisCustomFields = new Set(
        this.data().collections.flatMap((collection) =>
          collection.analyses.flatMap((analysis) => analysis.customFields ?? [])
        )
      );

      this.journeyIdFields.set(
        idFields
          .filter((field) => analysisCustomFields.has(field.fieldName))
          .map((field) => ({
            id: field.fieldName,
            title: field.description,
          }))
      );
    } catch (error) {
      this.alertService.showErrorToast(
        translate('new-analysis-group.failed-to-load-id-fields'),
        error
      );
    }
  }

  private sortData() {
    this.tableDataSource.data.sort((a, b) => {
      let ret = a.displayName?.localeCompare(b.displayName, undefined, { sensitivity: 'accent' });
      if (!ret) {
        ret = a.analysis?.configName?.localeCompare(b.analysis?.configName, undefined, {
          sensitivity: 'accent',
        });
      }
      if (!ret) {
        ret = a.analysis?.displayName?.localeCompare(b.analysis?.displayName, undefined, {
          sensitivity: 'accent',
        });
      }

      return ret;
    });

    this.rowSpans.set(this.calculateRowSpans());
  }

  private calculateRowSpans() {
    const rowSpans = [];
    let lastCollectionName;
    let lastCollectionIndex = 0;

    this.tableDataSource.data.forEach((row, index) => {
      if (lastCollectionName === row.collectionName) {
        rowSpans[lastCollectionIndex]++;
        rowSpans.push(0);
      } else {
        lastCollectionName = row.collectionName;
        lastCollectionIndex = index;
        rowSpans.push(1);
      }
    });
    return rowSpans;
  }

  onAnalysisSelectionChanged(element: CollectionTableRow, isSelected: boolean) {
    this.tableDataSource.data
      .filter((d) => d.collectionName === element.collectionName)
      .forEach((d) => {
        d.isSelected = false;
        d.doesNotHaveJourneyId = false;
        d.doesNotHaveDateData = false;
        d.doesNotHaveContentChannel = false;
      });

    element.isSelected = isSelected;
    element.doesNotHaveJourneyId =
      isSelected && this.analysisDoesNotHaveJourneyId(element.analysis);
    element.doesNotHaveDateData = isSelected && this.analysisDoesNotHaveDateData(element.analysis);
    element.doesNotHaveContentChannel =
      isSelected && this.collectionDoesNotHaveContentChannel(element.collectionContentChannel);
    this.updateGroupData();
  }

  private updateGroupData() {
    this.analysisGroupSelectionData.update((prev) => ({
      ...prev,
      collections: this.tableDataSource.data.filter((r) => r.isSelected),
    }));
  }

  onFilterFileAdded($event: { fileItem: FileItem }) {
    this.isFilterProcessing.set(true);

    const currentFile = this.filterFile();
    if (currentFile?.lookupId) {
      this.filterQueue.update((q) => [...q, currentFile.lookupId]);
    }

    this.filterFile.set({
      name: $event.fileItem.file.name,
      count: 0,
    });

    this.lookupService.createLookup(this.filterFile().name, $event.fileItem);
  }

  onFilterFileUploaded($event: Lookup) {
    this.isFilterProcessing.set(true);
    this.filterFile.set({
      name: $event.displayName,
      lookupId: $event.id,
      count: $event.values.length,
    });
    this.analysisService
      .getAnalysisDocumentCount(this.data().project.id, $event.id)
      .then((res) => {
        this.displayedColumns.update((cols) => {
          const updated = [...cols];
          updated.splice(4, 1, 'filtered-documents');
          return updated;
        });
        const newMap = new Map(this.filteredDocumentsByAnalysis());
        res.documentCounts.forEach((entry) => newMap.set(entry.value, entry.count));
        this.filteredDocumentsByAnalysis.set(newMap);
      })
      .finally(() => this.isFilterProcessing.set(false));
  }

  onFilterFileError() {
    this.isFilterProcessing.set(false);
  }

  onFilterFileRemove() {
    const currentFile = this.filterFile();
    if (currentFile?.lookupId) {
      this.filterQueue.update((q) => [...q, currentFile.lookupId]);
      this.displayedColumns.update((cols) => {
        const updated = [...cols];
        updated.splice(4, 1, 'documents');
        return updated;
      });
    }

    this.filterFile.set(undefined);
  }

  public async cancel() {
    const deleteSuccess = await this.clearFilterQueue(true);
    if (deleteSuccess) {
      this.modalClosed.emit();
    }
  }

  public async saveGroup(): Promise<void> {
    if (this.isSaving()) {
      return;
    }

    this.isSaving.set(true);

    this.analysisGroupSelectionData.update((prev) => ({
      ...prev,
      lookupId: this.filterFile()?.lookupId,
    }));
    const updatedSelection = this.analysisGroupSelectionData();
    const data = { name: updatedSelection.name, lookupId: updatedSelection.lookupId };

    try {
      const deleteSuccess = await this.clearFilterQueue();
      if (!deleteSuccess) {
        return;
      }

      if (this.inEditMode()) {
        await this.updateGroup(updatedSelection, data);
      } else {
        await this.createGroup(updatedSelection, data);
      }
      this.modalClosed.emit(updatedSelection);
    } catch (error: any) {
      if (
        this.isJourneyGroup() &&
        this.analysisGroupService.isJourneyGroupExceptionError(error.cause ?? error)
      ) {
        this.alertService.showErrorToast(
          translate(
            this.inEditMode()
              ? 'new-analysis-group.update-journey-group-failed'
              : 'new-analysis-group.create-journey-group-failed'
          )
        );
        return;
      }
      if (error instanceof AddAnalysesError) {
        this.alertService.showErrorToast(
          translate('analysis-group-service.failed-to-add-to-group'),
          error.cause
        );
        return;
      }
      throw error;
    } finally {
      this.isSaving.set(false);
    }
  }

  private async updateGroup(
    updatedSel: AnalysisGroupSelector,
    data: { name: string; lookupId?: string }
  ): Promise<void> {
    const groupId = this.data().analysisGroup.id;

    await this.analysisGroupService.updateAnalysisGroup(this.data().project.id, groupId, data);

    //Get array of analysis IDs currently in analysisGroup
    const savedAnalysisIds = this.data().analysisGroup.analyses.map(
      (analysis: any) => analysis.name || analysis.analysisName
    );
    const selectedAnalysisIds = updatedSel.collections.map(
      (collection) => collection.analysis.name
    );
    const savedIsJourneyGroup = !!this.data().analysisGroup.journeyGroupRule?.defaultField;
    const journeyGroupChanged = this.isJourneyGroup() !== savedIsJourneyGroup;
    const journeyDefaultIdChanged =
      updatedSel.journeyDefaultId !== this.data().analysisGroup.journeyGroupRule?.defaultField;
    const needsFullReset = journeyGroupChanged || journeyDefaultIdChanged;

    let idsToRemove = savedAnalysisIds.filter((id) => !selectedAnalysisIds.includes(id));
    let idsToAdd = selectedAnalysisIds.filter((id) => !savedAnalysisIds.includes(id));
    let removeDefaultId: string | undefined = updatedSel.journeyDefaultId;
    if (needsFullReset) {
      idsToRemove = savedAnalysisIds;
      idsToAdd = selectedAnalysisIds;
      removeDefaultId = this.data().analysisGroup.journeyGroupRule?.defaultField;
    }

    if (idsToRemove.length) {
      try {
        await this.analysisGroupService.removeAnalysesFromAnalysisGroup(
          this.data().project.id,
          groupId,
          idsToRemove,
          removeDefaultId
        );
      } catch (error: unknown) {
        throw new AddAnalysesError(error);
      }
    }

    if (idsToAdd.length) {
      try {
        await this.addAnalysesToAnalysisGroup(groupId, idsToAdd, updatedSel.journeyDefaultId);
      } catch (error: unknown) {
        // if we have an error we need to have a fallback to the original since we commited to remove
        await this.addAnalysesToAnalysisGroup(groupId, idsToRemove, removeDefaultId);
        throw new AddAnalysesError(error);
      }
    }
  }

  private async addAnalysesToAnalysisGroup(
    groupId: string,
    idsToAdd: string[],
    defaultField: string
  ): Promise<void> {
    await this.analysisGroupService.addAnalysesToAnalysisGroup(
      this.data().project.id,
      groupId,
      idsToAdd,
      defaultField
    );
  }

  private async createGroup(
    updatedSel: AnalysisGroupSelector,
    data: { name: string; lookupId?: string }
  ): Promise<void> {
    const group = await this.analysisGroupService.createAnalysisGroup(this.data().project.id, data);
    const selectedAnalysisIds = updatedSel.collections.map(
      (collection) => collection.analysis.name
    );
    if (selectedAnalysisIds.length) {
      try {
        await this.analysisGroupService.addAnalysesToAnalysisGroup(
          this.data().project.id,
          group.id,
          selectedAnalysisIds,
          updatedSel.journeyDefaultId
        );
      } catch (error: unknown) {
        try {
          await this.analysisGroupService.deleteAnalysisGroup(this.data().project.id, group.id);
        } catch {
          // service already shows error toast
        }
        throw new AddAnalysesError(error);
      }
    }
    const prevSelectedItem = this.localStorageService.getItem('lastProjectItemSelected');
    const newSelectedItem = {
      itemId: group.id,
      projectId: this.data().project.id,
      index: null,
      sortDir: prevSelectedItem.sortDir,
      sortProp: prevSelectedItem.sortProp,
    };

    this.localStorageService.setItem('lastProjectItemSelected', newSelectedItem);
  }

  private async clearFilterQueue(clearCurrent = false) {
    this.isFilterProcessing.set(true);

    const currentFile = this.filterFile();
    if (clearCurrent && currentFile?.lookupId) {
      this.filterQueue.update((q) => [...q, currentFile.lookupId]);
    }

    const lookupIdToKeep = this.analysisGroupSelectionData().lookupId;
    this.filterQueue.update((q) => q.filter((id) => id !== lookupIdToKeep));

    try {
      for (const id of this.filterQueue()) {
        await this.lookupService.deleteLookup(id);
      }
    } catch {
      return false;
    } finally {
      this.isFilterProcessing.set(false);
    }
    return true;
  }

  onNameChange(value: string) {
    this.analysisGroupSelectionData.update((prev) => ({ ...prev, name: value }));
    this.validateGroupName();
  }

  private validateGroupName() {
    let exsistingNames: string[] = [];
    const sel = this.analysisGroupSelectionData();

    if (!this.isSaving()) {
      exsistingNames = this.data().project.groups.reduce((names: string[], group) => {
        if (group.id !== sel.id) {
          names.push(group.name);
        }

        return names;
      }, []);
    }

    const status = { ...this.groupNameStatus() };
    generalNameValidator(
      sel.name,
      status,
      translate('new-analysis-group.analysis-group'),
      64,
      true,
      exsistingNames,
      false
    );
    this.groupNameStatus.set(status);
  }

  onJourneyDefaultIdChange(event: any) {
    this.analysisGroupSelectionData.update((prev) => ({
      ...prev,
      journeyDefaultId: event.detail.selected.id,
    }));
    this.setInvalidSourcesForJourney();
  }

  onJourneyGroupChange(): void {
    if (!this.isJourneyGroup()) {
      this.analysisGroupSelectionData.update((prev) => ({
        ...prev,
        journeyDefaultId: undefined,
      }));
    }
    this.setInvalidSourcesForJourney();
  }

  private saveDisabled(): boolean {
    const sel = this.analysisGroupSelectionData();
    if (
      !sel.name ||
      !this.selectedCollections().length ||
      this.groupNameStatus().state !== 'success'
    ) {
      return true;
    }

    if (this.isJourneyGroup()) {
      if (
        !sel.journeyDefaultId ||
        !sel.collections.some((row) => row.analysis.customFields?.includes(sel.journeyDefaultId)) ||
        sel.collections.some((row) => !row.analysis.hasDateData || !row.collectionContentChannel)
      ) {
        return true;
      }
    }

    if (!this.inEditMode()) {
      return false;
    }

    return !this.hasGroupChanged();
  }

  saveDisabledTooltip = computed<string | undefined>(() => {
    if (!this.isJourneyGroup()) {
      return undefined;
    }
    const sel = this.analysisGroupSelectionData();
    if (!sel.journeyDefaultId) {
      return translate('new-analysis-group.save-disabled-no-default-id');
    }
    const hasIdFieldInSelectedCollection = sel.collections.some((row) =>
      row.analysis.customFields?.includes(sel.journeyDefaultId)
    );
    if (!hasIdFieldInSelectedCollection && sel.collections.length > 0) {
      return translate('new-analysis-group.save-disabled-missing-id-field');
    }
    if (sel.collections.some((row) => !row.analysis.hasDateData)) {
      return translate('new-analysis-group.save-disabled-missing-date-data');
    }
    if (sel.collections.some((row) => !row.collectionContentChannel)) {
      return translate('new-analysis-group.save-disabled-missing-content-channel');
    }
    return undefined;
  });

  private hasGroupChanged(): boolean {
    const group = this.data()?.analysisGroup;
    if (!group) {
      return true;
    }

    const sel = this.analysisGroupSelectionData();
    if (sel.name !== group.name) {
      return true;
    }

    const savedIds = group.analyses.map((a) => a.name || a.analysisName);
    const selectedIds = sel.collections.map((c) => c.analysis.name);

    if (
      savedIds.length !== selectedIds.length ||
      !savedIds.every((id) => selectedIds.includes(id))
    ) {
      return true;
    }

    const savedIsJourneyGroup = !!group.journeyGroupRule?.defaultField;
    if (this.isJourneyGroup() !== savedIsJourneyGroup) {
      return true;
    }

    if (sel.journeyDefaultId !== group.journeyGroupRule?.defaultField) {
      return true;
    }

    return (this.filterFile()?.lookupId ?? undefined) !== (group.lookupId ?? undefined);
  }
}
